/**
 * weekMapping — pure date/week utilities for the tutor calendar.
 *
 * Uses native Date (no date-fns dependency) with explicit UTC-free
 * date arithmetic (local time only, matching server-side day strings).
 *
 * All "date" values produced/consumed here are 'YYYY-MM-DD' ISO strings
 * as returned by the API (session.date field from SessionResource).
 */

/**
 * Return the Monday (index 0) of the week containing `date`.
 * @param {Date} date
 * @returns {Date} Monday 00:00:00 local time
 */
export function getWeekStart(date) {
  const d = new Date(date);
  // getDay() returns 0=Sun, 1=Mon, ... 6=Sat
  const day = d.getDay();
  // How many days back to Monday: Sun→-6, Mon→0, Tue→-1, ...
  const diff = day === 0 ? -6 : 1 - day;
  d.setDate(d.getDate() + diff);
  d.setHours(0, 0, 0, 0);
  return d;
}

/**
 * Return the Sunday (index 6) of the week containing `date`.
 * @param {Date} date
 * @returns {Date} Sunday 23:59:59 local time
 */
export function getWeekEnd(date) {
  const start = getWeekStart(date);
  const end = new Date(start);
  end.setDate(end.getDate() + 6);
  end.setHours(23, 59, 59, 999);
  return end;
}

/**
 * Return an array of 7 Date objects [Mon, Tue, Wed, Thu, Fri, Sat, Sun]
 * for the week containing `date`.
 * @param {Date} date
 * @returns {Date[]}
 */
export function getWeekDays(date) {
  const start = getWeekStart(date);
  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(start);
    d.setDate(d.getDate() + i);
    return d;
  });
}

/**
 * Format a Date to 'YYYY-MM-DD' string (local time).
 * @param {Date} date
 * @returns {string}
 */
export function toDateString(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/**
 * Navigate to the previous week.
 * @param {Date} currentWeekStart - Monday of the current week
 * @returns {Date} Monday of the previous week
 */
export function prevWeek(currentWeekStart) {
  const d = new Date(currentWeekStart);
  d.setDate(d.getDate() - 7);
  return d;
}

/**
 * Navigate to the next week.
 * @param {Date} currentWeekStart - Monday of the current week
 * @returns {Date} Monday of the next week
 */
export function nextWeek(currentWeekStart) {
  const d = new Date(currentWeekStart);
  d.setDate(d.getDate() + 7);
  return d;
}

/**
 * Format a week label such as "14 – 20 jul. 2025".
 * @param {Date} weekStart - Monday
 * @returns {string}
 */
export function formatWeekLabel(weekStart) {
  const weekEnd = new Date(weekStart);
  weekEnd.setDate(weekEnd.getDate() + 6);

  const months = [
    'jan.', 'fév.', 'mar.', 'avr.', 'mai', 'jun.',
    'jul.', 'aoû.', 'sep.', 'oct.', 'nov.', 'déc.',
  ];

  const startDay = weekStart.getDate();
  const endDay = weekEnd.getDate();
  const endMonth = months[weekEnd.getMonth()];
  const endYear = weekEnd.getFullYear();

  if (weekStart.getMonth() === weekEnd.getMonth()) {
    return `${startDay} – ${endDay} ${endMonth} ${endYear}`;
  }
  const startMonth = months[weekStart.getMonth()];
  const startYear = weekStart.getFullYear();
  if (startYear === endYear) {
    return `${startDay} ${startMonth} – ${endDay} ${endMonth} ${endYear}`;
  }
  return `${startDay} ${startMonth} ${startYear} – ${endDay} ${endMonth} ${endYear}`;
}

/**
 * Given a flat list of sessions (from multiple contracts), group them by
 * day column index (0=Mon … 6=Sun) for the week starting at `weekStart`.
 *
 * Sessions outside the week are ignored.
 *
 * @param {Array<{date: string, [rest]: any}>} sessions - flat merged list
 * @param {Date} weekStart - Monday of the week
 * @returns {Object} { '0': [...], '1': [...], ..., '6': [...] }
 */
export function mapSessionsToWeek(sessions, weekStart) {
  const result = { 0: [], 1: [], 2: [], 3: [], 4: [], 5: [], 6: [] };

  const weekDays = getWeekDays(weekStart).map(toDateString);

  for (const session of sessions) {
    const idx = weekDays.indexOf(session.date);
    if (idx !== -1) {
      result[idx] = [...result[idx], session];
    }
  }

  // Sort each day's sessions by start_time
  for (const idx of Object.keys(result)) {
    result[idx].sort((a, b) => {
      if (a.start_time < b.start_time) return -1;
      if (a.start_time > b.start_time) return 1;
      return 0;
    });
  }

  return result;
}

/**
 * Check whether two 'YYYY-MM-DD' date strings are in the same week.
 * @param {string} dateStr
 * @param {Date} weekStart
 * @returns {boolean}
 */
export function isInWeek(dateStr, weekStart) {
  const weekDays = getWeekDays(weekStart).map(toDateString);
  return weekDays.includes(dateStr);
}

/**
 * French short day labels (Mon–Sun).
 */
export const WEEK_DAY_LABELS = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];
